import { StateNode } from "./canvas";
import { useValue } from "@tldraw/state-react";
import type { Editor } from "./canvas";
import { sheetAt } from "./sheet-operations";
import type { LibraryPreview } from "../shared/library";

export class LibraryPlacementTool extends StateNode {
  static override id = "library-place";
  commit?: (point: { x: number; y: number }) => void;
  onDone?: () => void;
  cancelFocus?: () => void;
  keyboardPoint?: { x: number; y: number };
  override onEnter(info: {
    commit: LibraryPlacementTool["commit"];
    exit: () => void;
    cancel?: () => void;
    keyboardPoint?: { x: number; y: number };
  }) {
    this.commit = info.commit;
    this.keyboardPoint = info.keyboardPoint;
    this.onDone = info.exit;
    this.cancelFocus = info.cancel;
    this.editor.setCursor({ type: "cross", rotation: 0 });
  }
  override onPointerMove() {
    const sheet = sheetAt(
      this.editor,
      this.editor.inputs.getCurrentPagePoint(),
    );
    this.editor.setHintingShapes(
      sheet &&
        !this.editor.isShapeHidden(sheet) &&
        !this.editor.isShapeOrAncestorLocked(sheet)
        ? [sheet.id]
        : [],
    );
  }
  override onPointerUp() {
    if (!this.editor.getIsReadonly())
      this.commit?.(this.editor.inputs.getCurrentPagePoint());
  }
  override onKeyDown(info: { key: string }) {
    if (info.key === "Enter")
      this.commit?.(this.keyboardPoint ?? this.editor.inputs.getCurrentPagePoint());
  }
  override onCancel() {
    const focus = this.cancelFocus;
    this.editor.setCurrentTool("select");
    focus?.();
  }
  override onExit() {
    this.editor.setHintingShapes([]);
    this.onDone?.();
    this.commit = undefined;
    this.keyboardPoint = undefined;
    this.onDone = undefined;
    this.cancelFocus = undefined;
  }
}

export function LibraryPlacementPreview({
  editor,
  preview,
}: {
  editor: Editor;
  preview?: LibraryPreview;
}) {
  const position = useValue(
    "library placement",
    () => {
      if (!editor.isIn("library-place")) return null;
      const point = editor.inputs.getCurrentPagePoint();
      const sheet = sheetAt(editor, point);
      const valid =
        !sheet ||
        (!editor.isShapeHidden(sheet) &&
          !editor.isShapeOrAncestorLocked(sheet));
      return {
        point: editor.pageToScreen(point),
        zoom: editor.getZoomLevel(),
        name: valid ? sheet?.props.name ?? "Canvas" : "Move onto the canvas",
        valid,
      };
    },
    [editor],
  );
  if (!position) return null;
  return (
    <div
      className={`library-ghost${position.valid ? " valid" : ""}`}
      aria-hidden="true"
      style={{
        left: position.point.x,
        top: position.point.y,
        width: (preview?.width ?? 180) * position.zoom,
        height: (preview?.height ?? 120) * position.zoom,
      }}
    >
      {preview?.image && <img src={preview.image} alt="" />}
      <span>{position.name}</span>
    </div>
  );
}
